import { useState } from "react";
import { Sparkles, Check, X, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function SeoPanel({ section, doc, editLang, onApply }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/seo/analyze");
      setAnalysis(data);
      if (!data.ai) toast.info("Score calcolato. Suggerimenti AI non disponibili al momento.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const ai = analysis?.ai;
  const suggestions = [];
  if (ai) {
    // Suggerimenti globali (meta title/description/keywords home): applicabili da qualsiasi sezione
    [
      ["title", "Meta title (home)"],
      ["description", "Meta description (home)"],
      ["keywords", "Keywords (home)"],
    ].forEach(([f, label]) => {
      const v = ai[`${f}_${editLang}`];
      if (v) suggestions.push({ key: f, label, value: v });
    });
    // Suggerimenti specifici di sezione
    if (section === "hero") {
      const v = ai[`hero_subtitle_${editLang}`];
      if (v) suggestions.push({ key: "hero-subtitle", label: "Sottotitolo hero", value: v });
    } else if (section === "servizi" && Array.isArray(ai.servizi_desc)) {
      ai.servizi_desc.forEach((s) => {
        const v = s[`desc_${editLang}`];
        const idx = (doc.servizi || []).findIndex((x) => x.id === s.id);
        if (v && idx >= 0) {
          const title = doc.servizi[idx].title;
          suggestions.push({
            key: `servizio-${s.id}`,
            label: `Descrizione · ${typeof title === "object" ? title?.[editLang] || title?.it : title}`,
            value: v,
            extra: { index: idx },
          });
        }
      });
    }
  }

  return (
    <div className="bg-surface border border-ice/8 rounded-xl p-5 space-y-5" data-testid="seo-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-azure" />
          <h2 className="font-display text-lg font-semibold">SEO Score & AI</h2>
        </div>
        <button
          data-testid="seo-analyze-button"
          onClick={analyze}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {loading ? "Analisi in corso…" : "Analizza con AI"}
        </button>
      </div>

      {analysis && (
        <>
          <div className="flex items-center gap-5">
            <span
              data-testid="seo-score-value"
              className={`font-display text-4xl font-extrabold ${analysis.score >= 80 ? "text-mint" : analysis.score >= 50 ? "text-gold" : "text-red-400"}`}
            >
              {analysis.score}
            </span>
            <div className="flex-1">
              <div className="h-2 rounded-full bg-onyx/80 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${analysis.score >= 80 ? "bg-mint" : analysis.score >= 50 ? "bg-gold" : "bg-red-400"}`}
                  style={{ width: `${analysis.score}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">Punteggio su 100 — basato sui contenuti attualmente salvati</p>
            </div>
          </div>

          <ul className="grid sm:grid-cols-2 gap-2">
            {analysis.checks.map((c) => (
              <li key={c.key} data-testid={`seo-check-${c.key}`} className="flex items-start gap-2 text-xs text-ice/80">
                {c.ok ? <Check size={14} className="text-mint shrink-0 mt-0.5" /> : <X size={14} className="text-red-400 shrink-0 mt-0.5" />}
                {c.label}
              </li>
            ))}
          </ul>

          {ai && (
            <div className="border-t border-ice/8 pt-5 space-y-4">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-azure">
                Suggerimenti AI ({editLang === "it" ? "Italiano" : "English"})
              </p>
              {suggestions.length > 0 ? (
                <>
                  {suggestions.length > 1 && (
                    <button
                      data-testid="seo-apply-all"
                      onClick={() => {
                        suggestions.forEach((f) => onApply(f.key, f.value, f.extra));
                        toast.success("Tutti i suggerimenti applicati — ricorda di salvare le modifiche");
                      }}
                      className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-xs font-semibold px-4 py-2 rounded-full"
                    >
                      <Check size={13} />
                      Applica tutti i suggerimenti
                    </button>
                  )}
                  {suggestions.map((f) => (
                  <div key={f.key} className="bg-onyx/60 border border-ice/8 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">{f.label}</span>
                      <button
                        data-testid={`seo-apply-${f.key}`}
                        onClick={() => {
                          onApply(f.key, f.value, f.extra);
                          toast.success("Suggerimento applicato — ricorda di salvare le modifiche");
                        }}
                        className="text-xs font-semibold text-azure hover:text-ice border border-royal/50 hover:border-azure rounded-full px-3 py-1 transition-colors"
                      >
                        Applica
                      </button>
                    </div>
                    <p className="text-sm text-ice/90 leading-relaxed">{f.value}</p>
                  </div>
                  ))}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Nessun suggerimento specifico per questa sezione: valgono le note qui sotto.</p>
              )}
              {Array.isArray(ai.notes) && ai.notes.length > 0 && (
                <ul data-testid="seo-ai-notes" className="space-y-2">
                  {ai.notes.map((n, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-xs text-muted-foreground">
                      <span className="mt-[5px] h-1 w-3 rounded-full bg-royal/60 shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      {!analysis && <p className="text-xs text-muted-foreground">Lancia l’analisi per ottenere il punteggio SEO dei contenuti e suggerimenti AI su title, description e keyword.</p>}
    </div>
  );
}
